'use client'

import { useWebOrdersQuery } from '@/app/[locale]/doi-tra/_hooks/react-query/use-web-orders'
import { toast } from '@/components/common/toast'
import { Alert } from '@/components/ui/alert'
import { useIsLoggedIn, useUserQuery } from '@/hooks/react-query/user/use-user'
import { cn } from '@/lib/utils'
import { __cExchangeService } from '@/services/client/__c.exchange.service'
import { WebOrder } from '@/types/exchange-order'
import { formatDate } from 'date-fns'
import { vi } from 'date-fns/locale'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

export const RecentOrderList = ({ className }: { className?: string }) => {
  const isLoggedIn = useIsLoggedIn()
  const router = useRouter()
  const { data: userData } = useUserQuery()
  const { data: webOrdersResponse, isLoading, error } = useWebOrdersQuery()
  const [isCheckingOrder, setIsCheckingOrder] = useState<string | null>(null)

  const handleOrderClick = async (orderCode: string) => {
    if (isCheckingOrder) return // Prevent multiple clicks

    setIsCheckingOrder(orderCode)

    try {
      const response =
        await __cExchangeService.checkGetAuthorizationCodeWebOrder(orderCode)

      if (response.state && response.data.authorization_code) {
        // Kiểm tra user có phone không
        if (userData?.phone) {
          // Success case - redirect to exchange page with authorization code
          const targetUrl = `/doi-tra/web/exchange?orderCode=${orderCode}&authorizationCode=${response.data.authorization_code}&phone=${userData.phone}`

          toast.message({
            title: 'Xác thực thành công!',
            message: 'Đang chuyển đến trang đổi hàng...',
          })

          router.push(targetUrl)
        } else {
          // User đã đăng nhập nhưng chưa có phone - chuyển đến verify-phone
          const targetUrl = `/doi-tra/web/verify-phone?orderCode=${orderCode}&authorizationCode=${response.data.authorization_code}`

          toast.message({
            title: 'Xác thực thành công!',
            message:
              'Tài khoản chưa có số điện thoại. Đang chuyển đến bước xác thực số điện thoại...',
          })

          router.push(targetUrl)
        }
      } else {
        // Error case - show alert
        toast.message({
          title: 'Không thể xác thực đơn hàng',
          message:
            response.message ||
            'Đơn hàng không tồn tại hoặc không thuộc tài khoản',
        })
      }
    } catch (error) {
      console.error('❌ Check authorization failed:', error)
      toast.message({
        title: 'Có lỗi xảy ra',
        message: 'Không thể xác thực đơn hàng. Vui lòng thử lại.',
      })
    } finally {
      setIsCheckingOrder(null)
    }
  }

  if (!isLoggedIn) return null

  // Lọc chỉ đơn hàng đã giao hàng thành công
  const filteredOrders =
    webOrdersResponse?.data?.orders?.filter(
      (order) => order.order_status === 'completed'
    ) || []

  const renderBody = () => {
    if (isLoading) {
      return [1, 2, 3].map((i) => (
        <div key={i} className='h-[50px] animate-pulse rounded-lg bg-white' />
      ))
    }

    if (error) {
      return (
        <Alert
          variant='secondary'
          title='Không thể tải danh sách đơn hàng. Vui lòng thử lại sau'
        />
      )
    }

    if (filteredOrders.length === 0) {
      return (
        <Alert title='Chưa có đơn hàng nào. Bạn chưa có đơn hàng Web nào đã giao thành công' />
      )
    }

    return filteredOrders.map((order: WebOrder) => (
      <button
        key={order.order_code}
        type='button'
        onClick={() => handleOrderClick(order.order_code)}
        disabled={!!isCheckingOrder}
        className={cn(
          'flex w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left transition-shadow hover:shadow-md',
          isCheckingOrder === order.order_code && 'opacity-50'
        )}
      >
        <div className='min-w-0'>
          <p className='truncate font-criteria text-sm text-neutral-900 md:text-base'>
            Đơn hàng{' '}
            <span className='text-primary-500'>#{order.order_code}</span>
          </p>
          <p className='text-xs text-neutral-700'>
            {order.total_quantity_items} sản phẩm:{' '}
            <span className='font-bold'>
              {order.total_price.toLocaleString('vi-VN')}đ
            </span>
          </p>
        </div>
        <div className='flex shrink-0 flex-col items-end gap-1'>
          <span className='rounded-full border border-green-600 px-2 py-0.5 text-[11px] leading-4 text-green-700'>
            Giao hàng thành công
          </span>
          <span className='text-xs text-neutral-700'>
            {isCheckingOrder === order.order_code
              ? 'Đang xác thực...'
              : formatDate(new Date(order.created_at), 'dd/MM/yyyy', {
                  locale: vi,
                })}
          </span>
        </div>
      </button>
    ))
  }

  return (
    <section className={cn('rounded-2xl bg-neutral-100 p-3', className)}>
      <h2 className='mb-3 font-criteria text-lg font-semibold tracking-[-0.02em] text-neutral-900 md:text-xl'>
        Đơn hàng gần đây
      </h2>
      <div className='max-h-[420px] space-y-1.5 overflow-y-auto'>
        {renderBody()}
      </div>
    </section>
  )
}
